import { useEffect, useState } from 'react'
import api from '../services/api'

// Afastamentos do funcionário (botão Status: WCMOTAFA/TMOTAFX do ERP, frota.motafx). Chave = data inicial.

interface Afastamento { ini: string; fim: string; afa: number; afades: string; usu: string; com: string }
interface Tipo { afacod: number; afades: string }
interface Form { iniAnterior: string; ini: string; fim: string; afa: string; com: string }

const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const VAZIO: Form = { iniAnterior: '', ini: '', fim: '', afa: '', com: '' }

export default function FuncionarioAfastamentosModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const [lista, setLista] = useState<Afastamento[] | null>(null)
  const [tipos, setTipos] = useState<Tipo[]>([])
  const [f, setF] = useState<Form>(VAZIO)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  const base = `/funcionarios/${encodeURIComponent(motcod)}/afastamentos`
  const deLinha = (x: Afastamento): Form => ({ iniAnterior: x.ini, ini: x.ini, fim: x.fim, afa: String(x.afa), com: x.com })

  async function carregar(manterIni?: string) {
    const r = await api.get(base)
    setLista(r.data)
    const alvo = r.data.find((x: Afastamento) => x.ini === manterIni)
    setF(alvo ? deLinha(alvo) : VAZIO)
  }

  useEffect(() => {
    api.get('/afastamentos').then(r => setTipos(r.data)).catch(() => {})
    carregar().catch(() => setErro('Não foi possível carregar os afastamentos do funcionário.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    if (!f.ini) return setErro('Informe a data inicial.')
    if (!f.afa) return setErro('Informe o tipo do afastamento.')
    setSalvando(true)
    try {
      const r = await api.put(base, { ...f, afa: Number(f.afa) })
      await carregar(r.data.ini)
      setAviso('Afastamento salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o afastamento.')
    } finally {
      setSalvando(false)
    }
  }

  async function excluir() {
    if (!f.iniAnterior || !window.confirm(`Excluir o afastamento iniciado em ${fmtData(f.iniAnterior)}?`)) return
    setErro(''); setAviso('')
    try {
      await api.delete(`${base}/${f.iniAnterior}`)
      await carregar()
      setAviso('Afastamento excluído.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao excluir o afastamento.')
    }
  }

  const set = (c: keyof Form, v: string) => setF(p => ({ ...p, [c]: v }))
  const th: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 820, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Afastamentos — {motcod} · {motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTAFX</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!lista ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <table className="table" style={{ width: '100%', margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Data Inicial</th>
                      <th style={th}>Data Final</th>
                      <th style={{ ...th, width: 50 }}>Código</th>
                      <th style={th}>Descrição do Afastamento</th>
                      <th style={th}>Usuário</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.length === 0 && (
                      <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum afastamento registrado.</td></tr>
                    )}
                    {lista.map(x => {
                      const ativo = x.ini === f.iniAnterior
                      return (
                        <tr key={x.ini} onClick={() => { setF(deLinha(x)); setErro(''); setAviso('') }}
                          style={{ cursor: 'pointer', background: ativo ? '#eef3fb' : undefined, fontWeight: ativo ? 600 : undefined }}>
                          <td style={td}>{fmtData(x.ini)}</td>
                          <td style={td}>{fmtData(x.fim) || 'Em aberto'}</td>
                          <td style={td}>{x.afa}</td>
                          <td style={td}>{x.afades}</td>
                          <td style={td}>{x.usu}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 0.6rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {f.iniAnterior ? `Afastamento de ${fmtData(f.iniAnterior)}` : 'Novo afastamento'}
                </span>
                {!!f.iniAnterior && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setF(VAZIO); setErro(''); setAviso('') }}>+ Novo afastamento</button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }}>
                <div className="field-cad" style={{ flex: '0 0 140px' }}>
                  <label>Data Inicial *</label>
                  <input type="date" value={f.ini} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('ini', e.target.value)} />
                </div>
                <div className="field-cad" style={{ flex: '0 0 140px' }}>
                  <label>Data Final</label>
                  <input type="date" value={f.fim} min={f.ini || undefined} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('fim', e.target.value)} />
                </div>
                <div className="field-cad" style={{ flex: '1 1 320px' }}>
                  <label>Afastamento *</label>
                  <select value={f.afa} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('afa', e.target.value)}>
                    <option value="">Selecione...</option>
                    {tipos.map(t => <option key={t.afacod} value={t.afacod}>{t.afacod} - {t.afades}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-cad" style={{ marginTop: '0.75rem' }}>
                <label>Comentário</label>
                <textarea value={f.com} rows={5} maxLength={800} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  onChange={e => set('com', e.target.value)} />
              </div>
            </>
          )}

          {lista && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          {!!f.iniAnterior && <button className="btn btn-secondary" style={{ marginRight: 'auto', color: '#dc2626' }} onClick={excluir}>Excluir</button>}
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !lista}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
